import type { PusherDeveloperBindings } from "../types";
import { decompressPusherResponseV2 } from "./decompressPusherResponseV2";
import { getChunkEventName } from "./getChunkEventName";
import { isPusherError } from "./types";

/**
 * Minimal shape that every realtime chunk must have so the accumulator can
 * track arrival order and detect when all chunks are in.
 * Domain-specific chunk types (e.g. CampaignQaPusherChunk) extend this.
 */
export interface PusherChunk {
  chunk_index: number;
  chunk_total: number;
}

export interface ChunkAccumulator {
  chunks: PusherChunk[];
  /** Resolves when chunks.length === chunk_total (all chunks received). */
  allChunksReceived: Promise<void>;
}

/**
 * Fields that the backend always sends on the chunked completion envelope.
 * The generic layer extracts these and passes them to the domain merge function.
 */
export interface ChunkedCompletionEnvelope {
  status: number;
  execution_time_ms?: number;
  error_code?: string;
  /**
   * Run-level id shared by this run's invocation logs. Carried through so domain
   * merges (e.g. campaign QA) can persist it as CampaignQaRun#run_id, which keys
   * the resolution audit join.
   */
  run_request_id?: string;
}

/**
 * Strategy for one chunked delivery flow.
 *
 * @template Chunk  The domain-specific chunk shape (extends PusherChunk)
 * @template Result The merged result shape returned to the caller
 *
 * @param completionKey  The boolean sentinel key on the Pusher completion event
 *   (e.g. "chunked_result_complete"). When this key is `true` on the
 *   decompressed payload, the generic layer treats it as a completion envelope.
 * @param merge  Assemble the accumulated chunks into the final result.
 *   Receives the envelope (status/timing/error fields) and the typed chunks.
 */
export interface ChunkedMergeConfig<Chunk extends PusherChunk = PusherChunk, Result = unknown> {
  completionKey: string;
  merge(envelope: ChunkedCompletionEnvelope, chunks: Chunk[]): Result;
}

// ---------------------------------------------------------------------------
// Strategy registry
// ---------------------------------------------------------------------------

const chunkedMergeStrategies = new Map<string, ChunkedMergeConfig>();

/**
 * Register a chunked merge strategy. Call from the hook body that uses the
 * strategy — idempotent registration ensures it runs before the POSTWS adapter
 * resolves it.
 */
export function registerChunkedMergeStrategy<C extends PusherChunk, R>(
  name: string,
  config: ChunkedMergeConfig<C, R>
): void {
  chunkedMergeStrategies.set(name, config as ChunkedMergeConfig);
}

export function resolveChunkedMergeConfig(name?: string): ChunkedMergeConfig | undefined {
  return name ? chunkedMergeStrategies.get(name) : undefined;
}

// ---------------------------------------------------------------------------
// Chunk subscription
// ---------------------------------------------------------------------------

function isPusherChunk(x: unknown): x is PusherChunk {
  return (
    typeof x === "object" &&
    x !== null &&
    typeof (x as PusherChunk).chunk_index === "number" &&
    typeof (x as PusherChunk).chunk_total === "number"
  );
}

/**
 * Subscribes to async_result_chunk_<job_token> and collects payloads.
 * Resolves `allChunksReceived` as soon as chunks.length === chunk_total,
 * so the caller can race it against the completion event and not lose
 * late-arriving chunks due to Pusher ordering variance.
 */
export function registerChunkSubscription(options: {
  enabled: boolean;
  pusher: PusherDeveloperBindings | undefined;
  jobToken: string | undefined;
  onCleanup: (fn: () => void) => void;
}): ChunkAccumulator {
  let resolveAllChunks: () => void;
  const allChunksReceived = new Promise<void>((resolve) => {
    resolveAllChunks = resolve;
  });
  const state: ChunkAccumulator = { chunks: [], allChunksReceived };

  const { jobToken, pusher } = options;
  if (!options.enabled || !jobToken || !pusher) {
    return state;
  }

  const chunkEventName = getChunkEventName(jobToken);
  // A job answers on both DUST and Pusher when tabs on each claimed it, and this tab listens on
  // both, so the same chunk can arrive twice.
  const receivedChunkIndexes = new Set<number>();
  const handler = (data: unknown) => {
    const dec = decompressPusherResponseV2(data);
    if (!isPusherError(dec) && isPusherChunk(dec) && !receivedChunkIndexes.has(dec.chunk_index)) {
      receivedChunkIndexes.add(dec.chunk_index);
      state.chunks.push(dec);
      if (dec.chunk_total > 0 && state.chunks.length >= dec.chunk_total) {
        resolveAllChunks();
      }
    }
  };
  pusher.bind(chunkEventName, handler);
  options.onCleanup(() => pusher.unbind(chunkEventName, handler));

  return state;
}

// ---------------------------------------------------------------------------
// Merge orchestration
// ---------------------------------------------------------------------------

const chunkStragglerTimeoutMs = 5_000;

function isCompletionPayload(decompressed: unknown, completionKey: string): boolean {
  return (
    typeof decompressed === "object" &&
    decompressed !== null &&
    (decompressed as Record<string, unknown>)[completionKey] === true
  );
}

function extractEnvelope(decompressed: unknown): ChunkedCompletionEnvelope {
  const raw = decompressed as Record<string, unknown>;
  return {
    status: (raw.status as number) ?? 0,
    execution_time_ms: raw.execution_time_ms as number | undefined,
    error_code: raw.error_code as string | undefined,
    run_request_id: raw.run_request_id as string | undefined,
  };
}

/**
 * After decompressing the main async_result payload, check whether it's a
 * chunked completion envelope. If so, wait for any outstanding chunks and
 * delegate to the config's merge function.
 */
export async function mergeChunkedPayloadIfNeeded<Result>(
  config: ChunkedMergeConfig | undefined,
  decompressed: unknown,
  chunkState: ChunkAccumulator
): Promise<Result> {
  if (config && isCompletionPayload(decompressed, config.completionKey)) {
    await waitForOutstandingChunks(chunkState);
    return config.merge(extractEnvelope(decompressed), chunkState.chunks) as Result;
  }
  return decompressed as Result;
}

/**
 * If we received the completion event but chunk_total indicates more chunks are
 * expected, wait briefly for stragglers (Pusher ordering edge case on reconnect).
 *
 * Also waits when zero chunks have arrived — the completion event may have arrived
 * before any chunk events due to Pusher ordering variance.
 */
async function waitForOutstandingChunks(chunkState: ChunkAccumulator): Promise<void> {
  const { chunks } = chunkState;
  if (chunks.length > 0) {
    const expectedTotal = chunks[0].chunk_total;
    if (expectedTotal > 0 && chunks.length >= expectedTotal) {
      return;
    }
  }
  await Promise.race([
    chunkState.allChunksReceived,
    new Promise<void>((resolve) => setTimeout(resolve, chunkStragglerTimeoutMs)),
  ]);
}
